import { useState } from 'react'

export default function PersonaSection({ journey, persona }) {
  const [expanded, setExpanded] = useState(true)

  const config = journey.persona_config || {}
  const metadata = persona?.metadata || {}
  const demographics = metadata.demographics || {}
  const archetype = metadata.archetype
  const clinicalAdditions = metadata.clinical_additions || {}
  const comorbidities = clinicalAdditions.comorbid_conditions || []
  const detailedProfile = persona?.detailed_profile

  return (
    <div className="card bg-base-100 shadow overflow-hidden">
      {/* Header */}
      <div
        className="p-4 cursor-pointer hover:bg-base-200 transition-colors"
        style={{ background: expanded ? undefined : 'linear-gradient(135deg, #667eea22 0%, #764ba222 100%)' }}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🧬</span>
            <h3 className="font-semibold text-lg">Patient Persona</h3>
          </div>
          <span className="text-base-content/40 text-lg">
            {expanded ? '▼' : '▶'}
          </span>
        </div>
        {!expanded && (
          <div className="flex flex-wrap gap-2 mt-2 ml-9">
            {config.type && <span className="badge badge-primary badge-sm">{config.type}</span>}
            {config.cause && <span className="badge badge-secondary badge-sm">{config.cause}</span>}
            {config.treatment && <span className="badge badge-accent badge-sm">{config.treatment}</span>}
            {archetype && <span className="badge badge-info badge-sm">{archetype}</span>}
          </div>
        )}
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-base-200 p-4 space-y-4">
          {/* Clinical config badges */}
          <div className="flex flex-wrap gap-2">
            {config.type && (
              <div className="badge badge-primary gap-1">
                <span className="opacity-60">Type:</span> {config.type}
              </div>
            )}
            {config.cause && (
              <div className="badge badge-secondary gap-1">
                <span className="opacity-60">Cause:</span> {config.cause}
              </div>
            )}
            {config.treatment && (
              <div className="badge badge-accent gap-1">
                <span className="opacity-60">Treatment:</span> {config.treatment}
              </div>
            )}
          </div>

          {/* Archetype badge */}
          {archetype && (
            <div className="flex items-center gap-2">
              <span className="badge badge-info badge-lg gap-1">
                <span className="opacity-60">Archetype:</span> {archetype}
              </span>
            </div>
          )}

          {/* Demographics row */}
          {(demographics.age_range || demographics.gender || demographics.occupation || demographics.socioeconomic_status) && (
            <div className="flex flex-wrap gap-2">
              {demographics.age_range && (
                <span className="badge badge-outline badge-sm gap-1">
                  <span className="opacity-60">Age:</span> {demographics.age_range}
                </span>
              )}
              {demographics.gender && (
                <span className="badge badge-outline badge-sm gap-1">
                  <span className="opacity-60">Gender:</span> {demographics.gender}
                </span>
              )}
              {demographics.occupation && (
                <span className="badge badge-outline badge-sm gap-1">
                  <span className="opacity-60">Occupation:</span> {demographics.occupation}
                </span>
              )}
              {demographics.socioeconomic_status && (
                <span className="badge badge-outline badge-sm gap-1">
                  <span className="opacity-60">SES:</span> {demographics.socioeconomic_status}
                </span>
              )}
            </div>
          )}

          {/* Comorbidities */}
          {comorbidities.length > 0 && (
            <div>
              <div className="text-xs text-base-content/50 mb-1">Comorbidities</div>
              <div className="flex flex-wrap gap-1">
                {comorbidities.map(c => (
                  <span key={c} className="badge badge-warning badge-sm">{c}</span>
                ))}
              </div>
            </div>
          )}

          {/* Detailed profile narrative */}
          {detailedProfile ? (
            <div className="bg-base-200 rounded-lg p-4 max-h-96 overflow-y-auto">
              <pre className="whitespace-pre-wrap text-sm font-sans leading-relaxed">
                {detailedProfile}
              </pre>
            </div>
          ) : (
            <p className="text-base-content/60 text-sm italic">
              {persona === null ? 'Loading persona details...' : 'Persona profile details are available via persona export.'}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
